import { ICheckItem, IChecklistItemRow, IStampSettings, TChecklistItemStatus } from "@mctypes/checklist.types";
import { IMinUserDepricated } from "@mctypes/global.types";
import { ActionIcon, Button, Divider, Group, Modal, Stack, Text, Textarea, Tooltip } from "@mantine/core";
import * as R from "ramda";
import React, { useEffect, useState } from "react";
import { formatDateYmdDotsWithTime } from "../admin/selectors";
import Avatar from "../../../components/Avatar";
import { useAppSelector } from "../../../hooks";
import InitialsAndDate from "./InitialsAndDate";

const StampCheck: React.FC<{
  disabled?: boolean;
  toggle: (rowId: string, oldValue: TChecklistItemStatus, newValue: TChecklistItemStatus) => void;
  updateCommentText?: (text: string) => void;
  checkItem: ICheckItem;
  row: IChecklistItemRow;
  stampSettings: IStampSettings;
  includeIr?: boolean;
  includeIrOnDefaultRoles?: boolean;
  includeUavOnDefaultRoles?: boolean;
  templateMode?: boolean;
  includeDisabled?: boolean;
  statusDisabled?: boolean;
}> = ({
  disabled,
  toggle,
  updateCommentText,
  checkItem,
  row,
  stampSettings,
  includeIr = false,
  includeIrOnDefaultRoles,
  includeUavOnDefaultRoles,
  templateMode,
  includeDisabled = false,
  statusDisabled = false,
}) => {
  const handleOnChange = (status: TChecklistItemStatus) => {
    toggle(row._id, checkItem.status, status);
  };

  const [commentModalOpen, setCommentModalOpen] = useState<boolean>(false);
  const user = useAppSelector((state) => state.globalReducer.user);

  const updatedBy = checkItem.updatedBy as IMinUserDepricated;
  // checkItem is null when N/A is set from template
  const hideComment =
    checkItem.status === "N/A" ||
    checkItem.updatedBy === null ||
    (checkItem.comment?.text === "" && updatedBy?._id !== user?._id);

  const onSaveComment = (text: string) => {
    updateCommentText(text);
    setCommentModalOpen(false);
  };

  return (
    <>
      {commentModalOpen === true && (
        <StampCheckModal
          onSaveCommentText={onSaveComment}
          checkItem={checkItem}
          toggle={() => setCommentModalOpen(false)}
        />
      )}
      {checkItem ? (
        row.originalItem != null && (
          <Group gap={4} align="center" style={{ display: disabled === true ? "none" : undefined }}>
            {templateMode !== true && (
              <>
                <Tooltip label="NEI" withArrow openDelay={400}>
                  <ActionIcon
                    aria-label="NEI"
                    variant="filled"
                    color={checkItem.status === "NO" ? stampSettings.no?.color : "warmgray.3"}
                    radius="xl"
                    size={25}
                    onClick={() => disabled === false && handleOnChange(checkItem.status === "NO" ? "N/A" : "NO")}
                  >
                    <i className={`fa fa-${stampSettings.no.icon}`} style={{ fontSize: "13px" }} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="OK" withArrow openDelay={400}>
                  <ActionIcon
                    aria-label="OK"
                    variant="filled"
                    color={checkItem.status === "YES" ? stampSettings.yes?.color : "warmgray.3"}
                    radius="xl"
                    size={25}
                    onClick={() => disabled === false && handleOnChange(checkItem.status === "YES" ? "N/A" : "YES")}
                  >
                    <i className={`fa fa-${stampSettings.yes.icon}`} style={{ fontSize: "13px" }} />
                  </ActionIcon>
                </Tooltip>
                {includeUavOnDefaultRoles === true && (
                  <Tooltip label="Uavklart" withArrow openDelay={400}>
                    <ActionIcon
                      aria-label="Uavklart"
                      variant="filled"
                      color={checkItem.status === "UAV" ? stampSettings.uav?.color : "warmgray.3"}
                      radius="xl"
                      size={25}
                      onClick={() => disabled === false && handleOnChange(checkItem.status === "UAV" ? "N/A" : "UAV")}
                    >
                      <i className={`fa fa-${stampSettings.uav.icon}`} style={{ fontSize: "13px" }} />
                    </ActionIcon>
                  </Tooltip>
                )}
              </>
            )}
            {(includeIr === true || includeIrOnDefaultRoles === true) && (
              <Tooltip label="Ikke relevant" withArrow openDelay={400}>
                <ActionIcon
                  aria-label="Ikke relevant"
                  variant="filled"
                  color={checkItem.status === "IR" ? stampSettings.ir?.color : "warmgray.3"}
                  radius="xl"
                  size={25}
                  onClick={() => disabled === false && handleOnChange(checkItem.status === "IR" ? "N/A" : "IR")}
                >
                  <i className={`fa fa-${stampSettings.ir?.icon}`} style={{ fontSize: "13px" }} />
                </ActionIcon>
              </Tooltip>
            )}
            {includeDisabled === true && (
              <ActionIcon
                variant="filled"
                color={checkItem.status === "DISABLED" ? "red" : "warmgray.3"}
                radius="xl"
                size={25}
                onClick={() =>
                  disabled === false && handleOnChange(checkItem.status === "DISABLED" ? "N/A" : "DISABLED")
                }
              >
                <i className="fa fa-times" style={{ fontSize: "13px" }} />
              </ActionIcon>
            )}
            {statusDisabled === false && (
              <div className="d-flex flex-column small-text overflow-hidden" style={{ height: "30px" }}>
                {checkItem.status !== "N/A" && (
                  <InitialsAndDate updatedBy={checkItem.updatedBy} updatedDate={checkItem.updatedDate} />
                )}
              </div>
            )}
            {hideComment === false && statusDisabled === false && (
              <Tooltip label="Interne kommentarer" withArrow openDelay={400}>
                <ActionIcon
                  aria-label="Interne kommentarer"
                  variant="filled"
                  color={checkItem.comment?.text?.length > 0 ? "blue" : "warmgray.3"}
                  radius="xl"
                  size={25}
                  onClick={() => setCommentModalOpen(true)}
                >
                  <i className="fa fa-comment" style={{ fontSize: "13px" }} />
                </ActionIcon>
              </Tooltip>
            )}
          </Group>
        )
      ) : (
        <div>{JSON.stringify(checkItem)}</div>
      )}
    </>
  );
};

export default StampCheck;

const StampCheckModal: React.FC<{
  toggle: () => void;
  checkItem: ICheckItem;
  onSaveCommentText: (text: string) => void;
}> = ({ toggle, checkItem, onSaveCommentText }) => {
  if (checkItem == null) {
    return null;
  }

  const [text, setText] = useState<string>("");

  const user = useAppSelector((state) => state.globalReducer.user);

  const userIsSameWhoUpdatedCheckItem = user._id === (checkItem.updatedBy as IMinUserDepricated)?._id;

  useEffect(() => {
    setText(R.clone(checkItem.comment?.text) || "");
  }, [checkItem.comment?.text]);

  const comment = checkItem.comment;

  const saveDisabled = text === checkItem.comment?.text;

  return (
    <Modal opened onClose={toggle} title="Angi kommentar" yOffset={240}>
      <Stack gap="sm">
        {comment?.text?.length > 0 && (
          <div className="d-flex align-content-center">
            <Avatar
              id={(comment.createdBy as IMinUserDepricated)?._id}
              name={(comment.createdBy as IMinUserDepricated)?.name}
            />
            <h5 className="gray-800-text mt-1">
              <b className="mr-2">{(comment.createdBy as IMinUserDepricated)?.name}</b>
            </h5>
            <div className="flex-fill" />
            <span>{formatDateYmdDotsWithTime(comment.createdDate)}</span>
          </div>
        )}
        {userIsSameWhoUpdatedCheckItem && (
          <Textarea value={text} onChange={(e) => setText(e.currentTarget.value)} autosize minRows={3} />
        )}
        {userIsSameWhoUpdatedCheckItem === false && <Text size="sm">{checkItem.comment?.text}</Text>}
        <Divider />
        <Group gap="xs" align="center">
          <i className="fa fa-info-circle gray-600-text" style={{ fontSize: "13px" }} />
          <Text size="xs" c="dimmed">
            Denne kommentaren er kun for internkommunikasjon og eksporteres ikke til Word-rapporten.
          </Text>
        </Group>

        {userIsSameWhoUpdatedCheckItem === true && (
          <Group justify="flex-end">
            <Button color="mc-primary" variant="filled" disabled={saveDisabled} onClick={() => onSaveCommentText(text)}>
              <i className="fa fa-save fa-fw" />
              Lagre
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
};
